import { useQuery } from '@apollo/client';

import { CHANNEL_PATH as PROPERTY_BILLS_CHANNEL_PATH } from '../../products/db_immo/constants.ts';
import { CHANNEL_PATH as DELIVERY_NOTES_CHANNEL_PATH } from '../../products/delivery_notes/constants.ts';
import { CHANNEL_PATH as INVOICES_CHANNEL_PATH } from '../../products/invoices/constants.ts';
import { CHANNEL_PATH as LIST_OF_SERVICES_CHANNEL_PATH } from '../../products/list_of_services/constants.ts';
import { CHANNEL_PATH as ORDER_CONFIRMATIONS_CHANNEL_PATH } from '../../products/order_confirmations/constants.ts';
import { CHANNEL_PATH as ORDERS_CHANNEL_PATH } from '../../products/orders/constants.ts';
import { CHANNEL_PATH as RFQS_CHANNEL_PATH } from '../../products/rfqs/constants.ts';
import { CHANNEL_PATH as UNIVERSAL_CHANNEL_PATH } from '../../products/universal/constants.ts';
import { CHANNEL_PRODUCTS, GET_MASTER_DATA_CHANNEL_USAGE } from '../queries.ts';

export interface LookupDefinitionChannelUsage {
    channelId: string;
    channelName: string;
    productKey: string;
    channelPath: string;
    configFields: string[];
}

const CHANNEL_PATHS: Record<string, string> = {
    rfqs: RFQS_CHANNEL_PATH,
    orders: ORDERS_CHANNEL_PATH,
    orderConfirmations: ORDER_CONFIRMATIONS_CHANNEL_PATH,
    invoices: INVOICES_CHANNEL_PATH,
    propertyBills: PROPERTY_BILLS_CHANNEL_PATH,
    deliveryNotes: DELIVERY_NOTES_CHANNEL_PATH,
    listOfServices: LIST_OF_SERVICES_CHANNEL_PATH,
    universal: UNIVERSAL_CHANNEL_PATH,
};

const collectConfigFieldsByLookupDefinitionId = (masterDataConfig): Map<string, string[]> => {
    const configFieldsById = new Map<string, string[]>();
    if (masterDataConfig == null) return configFieldsById;

    for (const [key, value] of Object.entries(masterDataConfig)) {
        if (!key.endsWith('LookupDefinitionId') || !value) continue;
        for (const id of Array.isArray(value) ? value : [value]) {
            const configFields = configFieldsById.get(id) || [];
            configFields.push(key);
            configFieldsById.set(id, configFields);
        }
    }
    return configFieldsById;
};

// There is no reverse "channels using this lookup definition" query, so the usage map is derived
// client-side by scanning every channel's masterDataConfig.
const useLookupDefinitionChannelUsage = () => {
    const { data, loading, error, refetch } = useQuery(GET_MASTER_DATA_CHANNEL_USAGE, {
        fetchPolicy: 'cache-and-network',
    });

    const usageByLookupDefinitionId = new Map<string, LookupDefinitionChannelUsage[]>();
    // All channels in navigation (document type) order
    const channels: LookupDefinitionChannelUsage[] = [];

    for (const { queryField, productKey } of CHANNEL_PRODUCTS) {
        const channelPath = CHANNEL_PATHS[productKey];
        for (const channel of data?.[queryField] || []) {
            channels.push({
                channelId: channel.id,
                channelName: channel.name,
                productKey,
                channelPath,
                configFields: [],
            });

            const configFieldsById = collectConfigFieldsByLookupDefinitionId(channel.masterDataConfig);
            for (const [lookupDefinitionId, configFields] of configFieldsById) {
                const usages = usageByLookupDefinitionId.get(lookupDefinitionId) || [];
                usages.push({
                    channelId: channel.id,
                    channelName: channel.name,
                    productKey,
                    channelPath,
                    configFields,
                });
                usageByLookupDefinitionId.set(lookupDefinitionId, usages);
            }
        }
    }

    return { usageByLookupDefinitionId, channels, loading, error, refetch };
};

export default useLookupDefinitionChannelUsage;
